import ReactGA from 'react-ga4';
import { Logger, LogType } from '@hitiq/logger';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { ModelTypes } from '../../dataStorePersistence/DataStoreModel';
import { Persistance } from '../../dataStorePersistence/persistence';
import { Translator } from '../translator.interface';
import { Conneqt, ConneqtBackend, ConneqtStatus } from './conneqt.interfaces';

export const createConneqtThunk = createAsyncThunk(
  'conneqt/createConneqt',
  async (
    payload: {
      translator: Translator<ConneqtBackend, Conneqt>;
      persistence: Persistance<ConneqtBackend>;
      loggingService: Logger;
      resource: Conneqt;
      existingConneqts: Conneqt[];
    },
    thunkAPI,
  ): Promise<any> => {
    const {
      translator,
      persistence,
      loggingService,
      resource,
      existingConneqts,
    } = payload;

    const conneqt = newConneqt(resource);

    // TODO: this is needed for conneqt since redux repository adds properties that conneqt doesn't have. Figure out if the properties are needed on the frontend at all and make sure it works without needing this check.
    const conneqtTranslated = translator.translateOneToBackendModel(conneqt);
    const backendResource = { ...conneqtTranslated };

    // remove undefined values only while creating a new conneqt
    Object.keys(backendResource).forEach(
      (key) =>
        backendResource[key] === undefined && delete backendResource[key],
    );

    if (
      hasPlayerConneqtCreatedByAccount(resource, existingConneqts) ||
      hasPlayerConneqtCreatedByOtherAccount(resource, existingConneqts)
    ) {
      return thunkAPI.rejectWithValue({
        heading: 'Unable to create conneqt',
        message:
          'conneqt instance already exists with the response accounts for this playerId',
        severity: 'error',
        id: uuidv4(),
      });
    }

    const result = await persistence.create(backendResource);
    loggingService.log(LogType.info, `Conneqt: ${result.data.id} created.`);

    if (result.error) {
      loggingService.log(
        LogType.error,
        `Conneqt: creation failed: ${result.error}`,
      );
      return onError(thunkAPI, result.error);
    }

    ReactGA.event({
      category: 'ConneQt',
      action: 'Create',
    });

    // WARNING: don't add response to redux here since onCreate will add it.
    return true;
  },
);

function newConneqt(resource: Conneqt): Conneqt {
  return {
    ...resource,
    id: 'not empty',
    modelType: ModelTypes.Conneqt,
    status: resource.status || ConneqtStatus.Created,
  };
}

function onError(thunkAPI: any, error: Error) {
  return thunkAPI.rejectWithValue({
    heading: 'Unable to create conneqt',
    message: error.message,
    severity: 'error',
    id: uuidv4(),
  });
}

// look for previous conneqt request with the same response account and player. So you don't create duplicate conneqts.
const hasPlayerConneqtCreatedByAccount = (
  conneqtInput: ConneqtBackend,
  existingConneqts: ConneqtBackend[],
) => {
  return existingConneqts.find(
    (_conneqt) =>
      _conneqt.responseAccountId === conneqtInput.responseAccountId &&
      _conneqt.requestPlayerId === conneqtInput.requestPlayerId,
  );
};

// look for previous conneqt request with the same request account and player. So you don't create duplicate conneqts.
const hasPlayerConneqtCreatedByOtherAccount = (
  conneqtInput: ConneqtBackend,
  existingConneqts: ConneqtBackend[],
) => {
  return existingConneqts.find(
    (_conneqt) =>
      _conneqt.accountId === conneqtInput.responseAccountId &&
      _conneqt.responsePlayerId === conneqtInput.requestPlayerId,
  );
};
